import {
    IonButton,
    IonRefresher,
    IonRefresherContent
} from "@ionic/react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { gridOutline, listOutline } from "ionicons/icons";
import { isNative } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../AuthProvider';
import { getAdjustedTiming, QueryTiming } from '../../../hooks/useQueryConfig';
import { Brand } from '../../../models/brand';
import BrandsService from '../../../services/brand.service';
import LoginButton from '../../ComponentsLogin/LoginButton/LoginButton';
import EclipticSearchbar from '../../ComponentsUI/EclipticSearchbar/EclipticSearchbar';
import IDXButtonIcon from '../../ComponentsUI/IDXButtonIcon/IDXButtonIcon';
import SearchProfilesLoading from '../../ComponentsUI/SearchProfilesLoading/SearchProfilesLoading';
import CrewCard from '../CrewCard/CrewCard';
import CrewList from '../CrewList/CrewList';

interface CrewListContainerProps { }

const CrewListContainer: React.FC<CrewListContainerProps> = () => {
    const { isAuthenticated } = useAuth();
    const [crewsFilter, setCrewsFilter] = useState<string>("");
    const [crewGrid, setCrewGrid] = useState<boolean>(!isNative);
    const [columnCount, setColumnCount] = useState(1);
    const parentRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();

    const { data: crews = [], isLoading: crewsLoading, refetch: refetchCrews } = useQuery({
        queryKey: ['crews'],
        queryFn: BrandsService.getAllBrands,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    useEffect(() => {
        const updateColumnCount = () => {
            const width = window.innerWidth;
            if (width >= 1280) setColumnCount(4); // xl
            else if (width >= 768) setColumnCount(2); // md
            else if (width >= 640) setColumnCount(2); // sm
            else setColumnCount(1); // xs
        };

        updateColumnCount();
        window.addEventListener('resize', updateColumnCount);
        return () => window.removeEventListener('resize', updateColumnCount);
    }, []);

    const filteredCrews = crews.filter((crew: Brand) =>
        crew.name.toLowerCase().includes(crewsFilter.toLowerCase())
    );

    const rowVirtualizer = useVirtualizer({
        count: crewGrid ? Math.ceil(filteredCrews.length / columnCount) : filteredCrews.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => crewGrid ? 300 : 100,
        overscan: 2,
    });

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: columnCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => parentRef.current ? parentRef.current.offsetWidth / columnCount : 300,
        overscan: 2,
    });

    const toggleCrewGrid = () => {
        setCrewGrid(!crewGrid);
    }

    useEffect(() => {
        if (!crewsLoading) {
            window.prerenderReady = true;
        }
    }, [crewsLoading]);

    const invalidateQueries = async (event: any) => {
        try {
            return refetchCrews();
        } finally {
            event.detail.complete();
        }
    }

    return (
        <>
            <div className="flex flex-row items-center mx-4 mt-2 justify-end mb-0.5">
                <IonRefresher slot="fixed" onIonRefresh={invalidateQueries}>
                    <IonRefresherContent />
                </IonRefresher>
                <div className="flex-1">
                    <EclipticSearchbar
                        onIonInput={(e) => setCrewsFilter(e.detail.value)}
                        showClearButton="focus"
                        showCancelButton="focus"
                        style={{ padding: 0 }}
                        id="searchText"
                        debounce={0}
                        animated={true}
                    />
                </div>
                <div>
                    <IonButton size="small" fill="clear" color="theme-secondary" onClick={toggleCrewGrid}>
                        <IDXButtonIcon size="text-2xl" icon={crewGrid ? listOutline : gridOutline} />
                    </IonButton>
                </div>
            </div>
            {!isAuthenticated ? (
                <div className="m-4">
                    <LoginButton
                        title="View The Crews, Squads, Communities, and Sponsors on Ecliptic"
                        upload={false}
                        profile={true}
                    />
                </div>
            ) : crewsLoading ? (
                <SearchProfilesLoading></SearchProfilesLoading>
            ) : filteredCrews.length === 0 ? (
                <div className="m-4 text-center">No crews found</div>
            ) : (
                <div
                    ref={parentRef}
                    style={{
                        height: '80vh',
                        overflow: 'auto',
                    }}
                >
                    <div
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                            <div
                                key={virtualRow.key}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${virtualRow.start}px)`,
                                    display: crewGrid ? 'flex' : 'block',
                                }}
                            >
                                {crewGrid ? (
                                    columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                                        const crewIndex = virtualRow.index * columnCount + virtualColumn.index;
                                        const crew = filteredCrews[crewIndex];
                                        if (!crew) return null;
                                        return (
                                            <div
                                                key={virtualColumn.key}
                                                style={{
                                                    width: `${100 / columnCount}%`,
                                                    padding: '8px',
                                                }}
                                            >
                                                <CrewCard crew={crew} />
                                            </div>
                                        );
                                    })
                                ) : (
                                    <CrewList crew={filteredCrews[virtualRow.index]} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default CrewListContainer;